import React, { useState } from 'react';
import Modal from '../../Modal';
import { RSButton, RSAlert } from 'reactsymbols-kit';
import Styles from '../../../styles/colors';
import type { IModelInformation } from '../../../stores/projects/model_information';
import { rsValue } from '../../Inspector/rsForm';

/** Props for the inner {@link RestoreVersionModal} content. */
interface RestoreVersionContentProps {
  /** The model whose version is restored. */
  model: IModelInformation;
  /** The id of the version to restore. */
  id: string;
  /** Clear the current selection before restoring. */
  clearSelection: () => void;
  /** Close the modal. */
  onRequestClose: () => void;
}

/** Props for the {@link RestoreVersionModalWrapper} (the default export). */
interface RestoreVersionModalProps extends RestoreVersionContentProps {
  /** Whether the modal is open. */
  isOpen: boolean;
}

/**
 * Restore a version, but first ask confirmation.
 */
function RestoreVersionModal({ model, id, clearSelection, onRequestClose }: RestoreVersionModalProps) {
  const [restoring, setRestoring] = useState(false);
  const [error, setError] = useState(false);

  // handler for the 'restore' button
  const onRestore = () => {
    setRestoring(true);
    setError(false);
    clearSelection();
    model
      .restoreVersion(id)
      .then(() => {
        onRequestClose();
      })
      .catch(() => {
        setRestoring(false);
        setError(true);
      });
  };

  // Legacy behaviour: the restore modal only opens for an existing version id,
  // so `findVersionById` always resolves here. The store interface types it as
  // possibly-undefined; assert non-null to keep byte-identical runtime without
  // adding a guard.
  const version = model.findVersionById(id)!;
  return (
    <div className="merlin-modal restore-version-modal">
      <div className="title">Restore '{version.name}'</div>
      <div className="modal-body">
        <div className="container">
          <div className="row">
            <div className="col">
              {error && (
                <RSAlert level="danger" visible={true}>
                  <p>We couldn't restore '{version.name}' because of an unexpected error.</p>
                </RSAlert>
              )}
              <p>
                The working version of your model will be replaced with the content of '
                {version.name}'.
              </p>
              <p> Are you sure you want to continue?</p>
            </div>
          </div>
        </div>
      </div>
      <div className="button-wrapper">
        <RSButton
          value="Cancel"
          type="button"
          background={Styles.defaultGradient}
          color={Styles.darkTextColor}
          onClick={onRequestClose}
        />
        <RSButton
          type="submit"
          background={Styles.blueGradient}
          value={rsValue(restoring ? null : 'Restore')}
          onClick={onRestore}
          iconName={rsValue(restoring ? 'FaSpinner' : null)}
          iconSize={15}
          className={rsValue(restoring ? 'spin' : null)}
          disabled={restoring}
        />
      </div>
    </div>
  );
}

// Wrap in a Modal
const RestoreVersionModalWrapper = ({ isOpen, onRequestClose, ...rest }: RestoreVersionModalProps) => (
  <Modal isOpen={isOpen} onRequestClose={onRequestClose}>
    <RestoreVersionModal isOpen={isOpen} onRequestClose={onRequestClose} {...rest} />
  </Modal>
);

export default RestoreVersionModalWrapper;
